<template>
  <nav class="app-shell-nav" :aria-label="$t('app.navMainAria', { shop: shopName })">
    <banner-superior v-if="showMarketingChrome" />
    <menu-superior
      v-if="showMarketingChrome"
      :device="device"
      :snat-client-access="snatClientAccess"
      :native-snat-tools-enabled="nativeSnatToolsOn"
      @open-site-menu="siteMenuOpen = true"
    />
    <menu-secundario
      v-if="showMarketingChrome"
      :device="device"
      :external-open="siteMenuOpen"
      @close-site-menu="siteMenuOpen = false"
    />
    <breadcrumb-component v-if="showBreadcrumbs" class="app-breadcrumb-wrap" />
  </nav>

  <div
    class="container-main"
    :class="{
      'container-main--mobile': device.isMobile,
      'container-main--pedido-cliente': layoutPedidoCliente,
    }"
  >
    <router-view :device="device" />
  </div>

  <footer-tienda v-if="showFooter" />
  <snat-unified-tools-panel v-if="showSnatUnifiedTools" />
  <snat-external-client-tools-entry v-if="showExternalSnatHub" />
  <theme-preference-modal />
  <button
    v-if="layoutMinimal"
    type="button"
    class="app-theme-fab"
    :class="{ 'app-theme-fab--pedido-cliente': layoutPedidoCliente }"
    :title="$t('appearance.toggleTitle')"
    :aria-label="$t('appearance.toggleAria')"
    @click="toggleAppearanceFab"
  >
    <i :class="['pi', appearanceFabIcon]" aria-hidden="true" />
  </button>
  <Toast position="top-center" />
</template>

<script>
import Toast from 'primevue/toast';
import BannerSuperior from './components/BannerSuperior.vue';
import MenuSuperior from './components/MenuSuperior.vue';
import MenuSecundario from './components/MenuSecundario.vue';
import BreadcrumbComponent from '@/components/BreadcrumbComponent.vue';
import FooterTienda from '@/components/FooterTienda.vue';
import SnatUnifiedToolsPanel from '@/components/SnatUnifiedToolsPanel.vue';
import SnatExternalClientToolsEntry from '@/components/SnatExternalClientToolsEntry.vue';
import ThemePreferenceModal from '@/components/ThemePreferenceModal.vue';
import AuthService from '@/services/AuthService';
import { resolveNotebookAccess } from '@/utils/notebookAccess';
import {
  snatNativeClientToolsEnabled,
  snatExternalHubVisible,
} from '@/utils/snatClientToolsRuntime';
import { sessionStore } from '@/store/session';
import { useAppearanceStore } from '@/store/appearance';

const breakpoints = {
  mobile: { until: 879 },
  tablet: { from: 880, until: 1023 },
  desktop: { from: 1024, until: 2408 },
  widescreen: { from: 1216, until: 1407 },
  fullhd: { from: 1408 },
};

export default {
  name: 'App',
  components: {
    Toast,
    MenuSuperior,
    BannerSuperior,
    MenuSecundario,
    BreadcrumbComponent,
    FooterTienda,
    SnatUnifiedToolsPanel,
    SnatExternalClientToolsEntry,
    ThemePreferenceModal,
  },
  data() {
    return {
      snatClientAccess: false,
      /** Reactividad al login/logout u otra pestaña (tokens en localStorage). */
      sessionRevision: 0,
      siteMenuOpen: false,
      appearanceRevision: 0,
      device: {
        isMobile: false,
        isTablet: false,
        isTabletOnly: false,
        isDesktop: false,
        isDesktopOnly: false,
        isWidescreen: false,
        isWidescreenOnly: false,
        isFullhd: false,
      },
    };
  },
  computed: {
    shopName() {
      return (process.env.VUE_APP_SHOP_NAME || 'KOSEXA').trim();
    },
    layoutPedidoCliente() {
      return !!(this.$route.meta && this.$route.meta.layoutPedidoCliente);
    },
    layoutMinimal() {
      if (this.layoutPedidoCliente) return true;
      const p = this.$route.path;
      return p === '/cv-mat' || p === '/login';
    },
    showMarketingChrome() {
      return !this.layoutMinimal;
    },
    showFooter() {
      return !this.layoutMinimal;
    },
    showBreadcrumbs() {
      if (this.layoutMinimal) return false;
      return this.$route.meta && this.$route.meta.showBreadcrumb === true;
    },
    hasOAuthSession() {
      void this.sessionRevision;
      const s = sessionStore();
      return !!(s.access_token || (typeof localStorage !== 'undefined' && localStorage.getItem('access_token')));
    },
    nativeSnatToolsOn() {
      return snatNativeClientToolsEnabled();
    },
    /** Panel único: errores + requerimientos UI + tickets SNAT (barra y esquina inferior). */
    showSnatUnifiedTools() {
      if (!this.nativeSnatToolsOn) return false;
      if (!this.hasOAuthSession || !this.snatClientAccess) return false;
      if (this.$route.path === '/login') return false;
      return true;
    },
    showExternalSnatHub() {
      if (!snatExternalHubVisible()) return false;
      if (!this.hasOAuthSession) return false;
      if (this.$route.path === '/login') return false;
      return true;
    },
    appearanceFabIcon() {
      void this.appearanceRevision;
      return useAppearanceStore().theme === 'light' ? 'pi-moon' : 'pi-sun';
    },
  },
  watch: {
    '$route.fullPath'() {
      this.sessionRevision += 1;
      this.refreshSnatClientAccess();
    },
  },
  methods: {
    async refreshSnatClientAccess() {
      try {
        const token =
          (typeof localStorage !== 'undefined' && localStorage.getItem('access_token')) || '';
        if (!token) {
          this.snatClientAccess = false;
          return;
        }
        this.snatClientAccess = await resolveNotebookAccess(AuthService);
      } catch {
        this.snatClientAccess = false;
      }
    },
    toggleAppearanceFab() {
      useAppearanceStore().toggleTheme();
    },
    onResize() {
      const w = window.innerWidth;
      this.device.isMobile = w <= breakpoints.mobile.until;
      this.device.isTablet = w >= breakpoints.tablet.from;
      this.device.isTabletOnly =
        w >= breakpoints.tablet.from && w <= breakpoints.tablet.until;
      this.device.isDesktop = w >= breakpoints.desktop.from;
      this.device.isDesktopOnly =
        w >= breakpoints.desktop.from && w <= breakpoints.desktop.until;
      this.device.isWidescreen = w >= breakpoints.widescreen.from;
      this.device.isWidescreenOnly =
        w >= breakpoints.widescreen.from && w <= breakpoints.widescreen.until;
      this.device.isFullhd = w >= breakpoints.fullhd.from;
    },
  },
  created() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
    this._kosexaAppearFab = () => {
      this.appearanceRevision += 1;
    };
    window.addEventListener('kosexa-appearance-changed', this._kosexaAppearFab);
    this._snatOnStorage = (ev) => {
      if (ev && (ev.key === 'access_token' || ev.key === 'refresh_token')) {
        this.sessionRevision += 1;
        this.refreshSnatClientAccess();
      }
    };
    this._snatOnOAuthSync = () => {
      this.sessionRevision += 1;
      this.refreshSnatClientAccess();
    };
    window.addEventListener('storage', this._snatOnStorage);
    window.addEventListener('kosexa-oauth-session-sync', this._snatOnOAuthSync);
  },
  async mounted() {
    useAppearanceStore().initWelcomeModal();
    this._snatOnVisibility = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        this.refreshSnatClientAccess();
      }
    };
    document.addEventListener('visibilitychange', this._snatOnVisibility);
    await this.refreshSnatClientAccess();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
    if (this._kosexaAppearFab) window.removeEventListener('kosexa-appearance-changed', this._kosexaAppearFab);
    if (this._snatOnStorage) window.removeEventListener('storage', this._snatOnStorage);
    if (this._snatOnOAuthSync) window.removeEventListener('kosexa-oauth-session-sync', this._snatOnOAuthSync);
    if (this._snatOnVisibility) document.removeEventListener('visibilitychange', this._snatOnVisibility);
  },
};
</script>

<style>
@import '~@/assets/css/main.css';

</style>
